import React from "react";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";

/**
 * "How GST Services works" — a marketing/explainer page reachable from the
 * upsell card on the sale form. Self-contained: all animation CSS is scoped
 * under the `gwx-` prefix in the <style> block below (no global SCSS changes).
 */
export function GstServicesInfoPage() {
    const navigate = useNavigate();
    return (
        <>
            <PageTitle breadcrumbs={[]}>GST Services</PageTitle>
            <GstServicesInfoContent onBack={() => navigate(-1)} />
        </>
    );
}

/**
 * The rich explainer content (hero + how it works + advantages + CTA), reusable
 * both as the standalone /gst-services-info page and inside the GST Services page
 * when the add-on is not enabled for the organization.
 */
export function GstServicesInfoContent({ onBack, light }) {
    const advantages = [
        { icon: "bi-lightning-charge-fill", title: "One-click e-Way bills", text: "The bill is generated on the government portal the moment a sale is created — no separate visit to the NIC site." },
        { icon: "bi-shield-lock-fill", title: "No SMS OTPs", text: "Operators never call the owner for a login code. Credentials stay on the server; the outpost just ticks a box." },
        { icon: "bi-receipt", title: "GST E-Invoice, automatically", text: "Every sale becomes a GST-compliant e-invoice with CGST / SGST / IGST worked out for you — zero tax math." },
        { icon: "bi-printer-fill", title: "Print-ready NIC format", text: "The official government layout with QR code and barcode, ready to hand to the driver." },
        { icon: "bi-clipboard2-check-fill", title: "Fails caught early", text: "A live checklist flags a bad GSTIN, pin code or HSN before you save — so paid portal calls don't get wasted." },
        { icon: "bi-arrow-repeat", title: "Fix & regenerate", text: "If the portal rejects a bill, correct the data on the device and regenerate — right from the sales list." },
    ];

    const steps = [
        { n: 1, title: "Record the sale", text: "Tick “Generate E-Way Bill” while creating the sale — just like Create Invoice." },
        { n: 2, title: "It syncs to the server", text: "RevCon’s server holds your GST portal credentials and talks to the government portal for you." },
        { n: 3, title: "Bill comes back", text: "The e-Way bill number appears on the sale within seconds — view, print or cancel from the outpost." },
    ];

    return (
        <>
            <style>{`
                .gwx-hero {
                    position: relative;
                    overflow: hidden;
                    border-radius: 16px;
                    padding: 40px;
                    background: linear-gradient(135deg, #1a1f36 0%, #2D2B55 55%, #1e1b4b 100%);
                    color: #fff;
                    box-shadow: 0 24px 48px rgba(0,0,0,0.22);
                }
                .gwx-orb { position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; }
                .gwx-orb-1 { width: 300px; height: 300px; top: -110px; right: -70px; background: rgba(99,91,255,0.35); }
                .gwx-orb-2 { width: 240px; height: 240px; bottom: -110px; left: -50px; background: rgba(66,165,245,0.25); }
                .gwx-badge {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 6px 14px; border-radius: 100px;
                    background: rgba(245,185,66,0.12); border: 1px solid rgba(245,185,66,0.4);
                    color: #F5B942; font-size: 12.5px; font-weight: 600; letter-spacing: 0.3px;
                }
                .gwx-title { font-size: 34px; line-height: 1.15; font-weight: 800; letter-spacing: -0.5px; margin: 16px 0 12px; }
                .gwx-grad { background: linear-gradient(90deg,#F5B942,#00D4AA); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
                .gwx-sub { color: rgba(255,255,255,0.8); font-size: 15px; line-height: 1.6; max-width: 560px; }
                .gwx-sub strong { color: #fff; }
                .gwx-stats { display: flex; gap: 22px; align-items: center; margin-top: 22px; padding: 14px 20px; width: fit-content; border-radius: 14px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); }
                .gwx-stat-num { font-size: 22px; font-weight: 800; line-height: 1.1; }
                .gwx-stat-num small { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.7); }
                .gwx-stat-label { font-size: 12px; color: rgba(255,255,255,0.6); }
                .gwx-divider { width: 1px; height: 34px; background: rgba(255,255,255,0.15); }
                .gwx-svg { width: 100%; height: auto; display: block; }

                /* Light-blue premium variant (used on the GST Services page) */
                .gwx-hero--light {
                    background:
                        radial-gradient(1100px 420px at 12% -15%, rgba(120,165,255,0.32), transparent 60%),
                        radial-gradient(820px 380px at 92% 118%, rgba(80,205,137,0.16), transparent 55%),
                        linear-gradient(135deg, #f4f8ff 0%, #e3ecff 55%, #eef4ff 100%);
                    color: #1a1f36;
                    border: 1px solid rgba(120,165,255,0.40);
                    box-shadow: 0 22px 52px rgba(66,120,245,0.22), inset 0 1px 0 rgba(255,255,255,0.9);
                }
                .gwx-hero--light .gwx-sub { color: #48566b; }
                .gwx-hero--light .gwx-sub strong { color: #1a1f36; }
                .gwx-hero--light .gwx-stats { background: rgba(255,255,255,0.68); border-color: rgba(120,165,255,0.35); box-shadow: 0 8px 22px rgba(66,120,245,0.12); }
                .gwx-hero--light .gwx-stat-num { color: #1a1f36; }
                .gwx-hero--light .gwx-stat-num small { color: #5a6a85; }
                .gwx-hero--light .gwx-stat-label { color: #6a7a95; }
                .gwx-hero--light .gwx-divider { background: rgba(26,31,54,0.10); }
                .gwx-hero--light .gwx-badge { background: #fff6df; border-color: rgba(245,185,66,0.55); color: #b58f00; box-shadow: 0 2px 8px rgba(245,185,66,0.18); }
                .gwx-hero--light .gwx-orb { display: none; }

                @keyframes gwx-flow { to { stroke-dashoffset: -13; } }
                .gwx-flow { animation: gwx-flow 1.1s linear infinite; }
                @keyframes gwx-pulse { 0% { transform: scale(1); opacity: 0.9; } 70%,100% { transform: scale(1.5); opacity: 0; } }
                .gwx-pulse { transform-origin: 272px 108px; animation: gwx-pulse 2.6s ease-out infinite; }
                @keyframes gwx-qr { 0% { opacity: 0; } 18%,88% { opacity: 1; } 100% { opacity: 0.35; } }
                .gwx-qr rect { animation: gwx-qr 5.2s ease-in-out infinite; }
                .gwx-qr rect:nth-child(2n) { animation-delay: 0.25s; }
                .gwx-qr rect:nth-child(3n) { animation-delay: 0.5s; }
                .gwx-qr rect:nth-child(5n) { animation-delay: 0.75s; }
                @keyframes gwx-pop { 0%,18% { transform: scale(0); } 30%,92% { transform: scale(1); } 100% { transform: scale(0); } }
                .gwx-pop { transform-origin: 460px 34px; animation: gwx-pop 5.2s cubic-bezier(0.34,1.56,0.64,1) infinite; }
                @keyframes gwx-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
                .gwx-float { animation: gwx-float 5s ease-in-out infinite; }
                .gwx-float-alt { animation: gwx-float 5s ease-in-out infinite; animation-delay: -2.5s; }
                @keyframes gwx-write { 0% { stroke-dashoffset: 95; } 30%,85% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; opacity: 0; } }
                .gwx-write { stroke-dasharray: 95; stroke-dashoffset: 95; animation: gwx-write 4.5s ease-in-out infinite; }
                .gwx-write-2 { animation-delay: 0.35s; }
                .gwx-write-3 { animation-delay: 0.7s; }
                @keyframes gwx-road { to { stroke-dashoffset: -32; } }
                .gwx-road { animation: gwx-road 0.9s linear infinite; }
                @keyframes gwx-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-1.5px); } }
                .gwx-truck-body { animation: gwx-bob 1.6s ease-in-out infinite; }
                @keyframes gwx-wheel { to { transform: rotate(360deg); } }
                .gwx-wheel { animation: gwx-wheel 1.4s linear infinite; }
                @keyframes gwx-otp { 0%,30% { stroke-dashoffset: 60; } 45%,88% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; opacity: 0; } }
                .gwx-otp-slash { stroke-dasharray: 60; stroke-dashoffset: 60; animation: gwx-otp 4s ease-in-out infinite; }
                .gwx-dot { animation: gwx-dotmove 2.6s linear infinite; }
                @keyframes gwx-dotmove { 0% { transform: translateX(0); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateX(86px); opacity: 0; } }
                @media (prefers-reduced-motion: reduce) {
                    .gwx-flow,.gwx-pulse,.gwx-qr rect,.gwx-pop,.gwx-float,.gwx-float-alt,.gwx-write,.gwx-road,.gwx-truck-body,.gwx-wheel,.gwx-otp-slash,.gwx-dot { animation: none; }
                    .gwx-write { stroke-dashoffset: 0; } .gwx-pop { transform: scale(1); } .gwx-otp-slash { stroke-dashoffset: 0; }
                }
            `}</style>

            {/* Hero */}
            <div className={`gwx-hero mb-5 ${light ? "gwx-hero--light" : ""}`}>
                <div className="gwx-orb gwx-orb-1"></div>
                <div className="gwx-orb gwx-orb-2"></div>
                <div className="row align-items-center position-relative">
                    <div className="col-lg-6">
                        <span className="gwx-badge"><i className="bi bi-gem"></i> Premium — only on RevCon</span>
                        <div className="gwx-title">GST E-Invoice &amp; e-Way bill<br /><span className="gwx-grad">in one click</span></div>
                        <p className="gwx-sub">
                            Every sale becomes a GST-compliant invoice automatically — and the e-Way bill is generated on
                            the government portal straight from RevCon. No portal logins, no SMS OTP calls to the owner,
                            no re-typing. <strong>No other platform in this industry does this.</strong>
                        </p>
                        <div className="gwx-stats">
                            <div>
                                <div className="gwx-stat-num">~20 <small>min</small></div>
                                <div className="gwx-stat-label">saved per sale</div>
                            </div>
                            <div className="gwx-divider"></div>
                            <div>
                                <div className="gwx-stat-num">0</div>
                                <div className="gwx-stat-label">OTPs &amp; logins</div>
                            </div>
                            <div className="gwx-divider"></div>
                            <div>
                                <div className="gwx-stat-num">100%</div>
                                <div className="gwx-stat-label">GST compliant</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 mt-4 mt-lg-0">
                        <FlowSvg light={light} />
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div className="mb-3">
                <h3 className="fw-bold text-gray-900 mb-1">How it works</h3>
                <p className="text-muted">Three steps — the operator only does the first one.</p>
            </div>
            <div className="row g-4 mb-5">
                {steps.map((s) => (
                    <div className="col-md-4" key={s.n}>
                        <div className="card border-0 shadow-sm h-100 position-relative overflow-hidden">
                            <StepMotif n={s.n} />
                            <div className="card-body p-4 position-relative">
                                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                    style={{ width: 42, height: 42, background: "linear-gradient(135deg,#635BFF,#42A5F5)", color: "#fff", fontWeight: 800 }}>
                                    {s.n}
                                </div>
                                <div className="fw-bold text-gray-900 mb-1">{s.title}</div>
                                <div className="text-muted small">{s.text}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Advantages */}
            <div className="mb-3">
                <h3 className="fw-bold text-gray-900 mb-1">Why it saves you time</h3>
                <p className="text-muted">Everything the manual e-Way bill process makes you do, RevCon does for you.</p>
            </div>
            <div className="row g-4 mb-5">
                {advantages.map((a, i) => (
                    <div className="col-md-6 col-xl-4" key={i}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body p-4 d-flex">
                                <div className="d-inline-flex align-items-center justify-content-center rounded flex-shrink-0 me-3"
                                    style={{ width: 40, height: 40, background: "rgba(99,91,255,0.1)", color: "#635BFF" }}>
                                    <i className={`bi ${a.icon} fs-4`}></i>
                                </div>
                                <div>
                                    <div className="fw-bold text-gray-900 mb-1">{a.title}</div>
                                    <div className="text-muted small">{a.text}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* What happens after it's enabled */}
            <div className="d-flex align-items-start rounded p-3 mb-4" style={{ background: "#eef6ff", border: "1px solid #d6e9ff" }}>
                <i className="bi bi-arrow-repeat text-primary me-3 mt-1 fs-4"></i>
                <div className="small text-gray-700">
                    <span className="fw-bold text-gray-900">Once GST Services is enabled for your organization,</span> this
                    same <strong>GST Services</strong> screen becomes your control panel. Come back here any time to add or
                    update your government portal login, run <strong>Check Auth</strong> to confirm it works, and manage
                    your GST settings and usage.
                </div>
            </div>

            {/* CTA */}
            <div className="card border-0 shadow-sm mb-5" style={{ background: "linear-gradient(135deg,#1a1f36,#2D2B55)" }}>
                <div className="card-body p-4 d-flex flex-wrap align-items-center justify-content-between gap-3 text-white">
                    <div>
                        <div className="fw-bold fs-5">Ready to skip the portal?</div>
                        <div className="small" style={{ color: "rgba(255,255,255,0.75)" }}>
                            <i className="bi bi-telephone me-2"></i>Contact RevCon at{" "}
                            <a href="tel:+919187214264" className="fw-semibold text-warning text-nowrap">+91 91872 14264</a>{" "}
                            to enable GST Services for your plant.
                        </div>
                    </div>
                    {onBack && (
                        <button className="btn btn-light" onClick={onBack}>
                            <i className="bi bi-arrow-left me-2"></i>Back
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}

/** Small decorative animated motif in the top-right of each "How it works" step card. */
function StepMotif({ n }) {
    const wrap = {
        className: "position-absolute",
        style: { top: 12, right: 12, opacity: 0.13, pointerEvents: "none" },
        width: 66, height: 66, viewBox: "0 0 64 64", "aria-hidden": true,
    };
    if (n === 1) {
        // A document with lines "writing" in — recording the sale.
        return (
            <svg {...wrap}>
                <rect x="16" y="8" width="32" height="46" rx="4" fill="none" stroke="#635BFF" strokeWidth="2.5" />
                <line x1="22" y1="20" x2="42" y2="20" stroke="#635BFF" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="20" strokeDashoffset="20">
                    <animate attributeName="stroke-dashoffset" values="20;0;0" keyTimes="0;0.5;1" dur="3s" repeatCount="indefinite" />
                </line>
                <line x1="22" y1="28" x2="42" y2="28" stroke="#635BFF" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="20" strokeDashoffset="20">
                    <animate attributeName="stroke-dashoffset" values="20;20;0;0" keyTimes="0;0.25;0.6;1" dur="3s" repeatCount="indefinite" />
                </line>
                <line x1="22" y1="36" x2="36" y2="36" stroke="#635BFF" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="14" strokeDashoffset="14">
                    <animate attributeName="stroke-dashoffset" values="14;14;0;0" keyTimes="0;0.4;0.75;1" dur="3s" repeatCount="indefinite" />
                </line>
            </svg>
        );
    }
    if (n === 2) {
        // Rotating sync arrows — syncing to the server.
        return (
            <svg {...wrap}>
                <g>
                    <path d="M32 12 A18 18 0 0 1 50 30 L45 30 L52 40 L59 30 L54 30 A22 22 0 0 0 32 8 Z" fill="#42A5F5" />
                    <path d="M32 48 A18 18 0 0 1 14 30 L19 30 L12 20 L5 30 L10 30 A22 22 0 0 0 32 52 Z" fill="#42A5F5" />
                    <animateTransform attributeName="transform" type="rotate" from="0 32 30" to="360 32 30" dur="4s" repeatCount="indefinite" />
                </g>
            </svg>
        );
    }
    // Checkmark drawing inside a ring — the bill comes back verified.
    return (
        <svg {...wrap}>
            <circle cx="32" cy="30" r="20" fill="none" stroke="#17C653" strokeWidth="2.5" />
            <path d="M22 30 L29 37 L43 22" fill="none" stroke="#17C653" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="34" strokeDashoffset="34">
                <animate attributeName="stroke-dashoffset" values="34;34;0;0;34" keyTimes="0;0.2;0.5;0.85;1" dur="3.5s" repeatCount="indefinite" />
            </path>
        </svg>
    );
}

/** Animated story: sale invoice → government portal → e-Way bill, plus time saved, no-OTP and a rolling truck. */
function FlowSvg({ light }) {
    // On the light-blue hero the white labels/strips would vanish — swap to dark tones.
    const portalLabel = light ? "#48566b" : "rgba(255,255,255,0.75)";
    const stripBg = light ? "rgba(66,120,245,0.07)" : "rgba(255,255,255,0.07)";
    const stripStroke = light ? "rgba(66,120,245,0.22)" : "rgba(255,255,255,0.18)";
    const stripHeading = light ? "#1a1f36" : "#ffffff";
    const stripSub = light ? "#6a7a95" : "rgba(255,255,255,0.65)";
    const roadBase = light ? "rgba(26,31,54,0.18)" : "rgba(255,255,255,0.25)";
    const roadDash = light ? "rgba(26,31,54,0.40)" : "rgba(255,255,255,0.55)";
    return (
        <svg className="gwx-svg" viewBox="0 0 480 430" fill="none" xmlns="http://www.w3.org/2000/svg"
            role="img" aria-label="A sale becomes a GST E-Invoice, the e-Way bill is generated on the government portal, and the truck is ready to move">
            {/* Invoice */}
            <g className="gwx-float">
                <rect x="28" y="34" width="118" height="148" rx="10" fill="#ffffff" opacity="0.97" />
                <rect x="28" y="34" width="118" height="30" rx="10" fill="#635BFF" />
                <rect x="28" y="54" width="118" height="10" fill="#635BFF" />
                <text x="87" y="54" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="700" fill="#ffffff">GST E-INVOICE</text>
                <text x="42" y="88" fontFamily="Inter, sans-serif" fontSize="15" fontWeight="800" fill="#0a2540">&#8377;</text>
                <line className="gwx-write" x1="60" y1="84" x2="132" y2="84" stroke="#c7d2fe" strokeWidth="5" strokeLinecap="round" />
                <line className="gwx-write gwx-write-2" x1="42" y1="104" x2="132" y2="104" stroke="#e3e8ee" strokeWidth="5" strokeLinecap="round" />
                <line className="gwx-write gwx-write-3" x1="42" y1="122" x2="110" y2="122" stroke="#e3e8ee" strokeWidth="5" strokeLinecap="round" />
                <rect x="42" y="156" width="52" height="12" rx="6" fill="#00D4AA" opacity="0.25" />
                <text x="68" y="165.5" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="700" fill="#00D4AA">AUTO TAX</text>
            </g>

            {/* Flow invoice -> portal */}
            <path d="M150 108 H236" stroke="#42A5F5" strokeWidth="2.5" strokeDasharray="6 7" strokeLinecap="round" className="gwx-flow" />
            <circle cx="150" cy="108" r="4.5" fill="#42A5F5" className="gwx-dot" />

            {/* Government portal */}
            <g>
                <circle className="gwx-pulse" cx="272" cy="108" r="34" stroke="#42A5F5" strokeWidth="1.5" fill="none" />
                <circle cx="272" cy="108" r="34" fill="#0f1b3d" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                <path d="M258 104 L272 93 L286 104 Z" fill="#42A5F5" />
                <rect x="259" y="106" width="4.5" height="14" rx="1.5" fill="#8ec9f8" />
                <rect x="267" y="106" width="4.5" height="14" rx="1.5" fill="#8ec9f8" />
                <rect x="275" y="106" width="4.5" height="14" rx="1.5" fill="#8ec9f8" />
                <rect x="283" y="106" width="4.5" height="14" rx="1.5" fill="#8ec9f8" />
                <rect x="256" y="120" width="32" height="4" rx="2" fill="#42A5F5" />
                <text x="272" y="160" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" fill={portalLabel}>Govt. e-Way Portal</text>
            </g>

            {/* Flow portal -> e-way bill */}
            <path d="M308 108 H356" stroke="#00D4AA" strokeWidth="2.5" strokeDasharray="6 7" strokeLinecap="round" className="gwx-flow" />
            <circle cx="308" cy="108" r="4.5" fill="#00D4AA" className="gwx-dot" style={{ animationDelay: "1.3s" }} />

            {/* e-Way bill with QR */}
            <g className="gwx-float-alt">
                <rect x="356" y="30" width="106" height="156" rx="10" fill="#ffffff" opacity="0.97" />
                <text x="409" y="52" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10.5" fontWeight="800" fill="#0a2540" letterSpacing="0.5">E-WAY BILL</text>
                <line x1="368" y1="60" x2="450" y2="60" stroke="#e3e8ee" strokeWidth="1.5" strokeDasharray="3 3" />
                <g className="gwx-qr" fill="#0a2540">
                    <rect x="382" y="70" width="12" height="12" rx="1.5" />
                    <rect x="424" y="70" width="12" height="12" rx="1.5" />
                    <rect x="382" y="112" width="12" height="12" rx="1.5" />
                    <rect x="400" y="76" width="7" height="7" rx="1" />
                    <rect x="411" y="70" width="7" height="7" rx="1" />
                    <rect x="400" y="90" width="7" height="7" rx="1" />
                    <rect x="413" y="92" width="10" height="10" rx="1" />
                    <rect x="428" y="90" width="7" height="7" rx="1" />
                    <rect x="400" y="105" width="7" height="7" rx="1" />
                    <rect x="411" y="115" width="7" height="7" rx="1" />
                    <rect x="428" y="105" width="7" height="7" rx="1" />
                    <rect x="424" y="117" width="10" height="7" rx="1" />
                </g>
                <g fill="#0a2540" opacity="0.85">
                    <rect x="374" y="136" width="2.5" height="18" /><rect x="379" y="136" width="1.5" height="18" /><rect x="384" y="136" width="3.5" height="18" /><rect x="390" y="136" width="1.5" height="18" /><rect x="395" y="136" width="2.5" height="18" /><rect x="401" y="136" width="4" height="18" /><rect x="408" y="136" width="1.5" height="18" /><rect x="412" y="136" width="2.5" height="18" /><rect x="418" y="136" width="1.5" height="18" /><rect x="423" y="136" width="3.5" height="18" /><rect x="429" y="136" width="1.5" height="18" /><rect x="434" y="136" width="2.5" height="18" /><rect x="440" y="136" width="4" height="18" />
                </g>
                <text x="409" y="172" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="600" fill="#425466" letterSpacing="1">1310 1214 0775</text>
                <g className="gwx-pop">
                    <circle cx="460" cy="34" r="13" fill="#00D4AA" />
                    <path d="M454 34 L458.5 38.5 L466 30.5" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </g>
            </g>

            {/* Stopwatch strip */}
            <g className="gwx-float">
                <rect x="30" y="228" width="200" height="58" rx="14" fill={stripBg} stroke={stripStroke} />
                <circle cx="62" cy="257" r="17" fill="none" stroke="#F5B942" strokeWidth="2.5" />
                <rect x="58.5" y="234" width="7" height="5" rx="1.5" fill="#F5B942" />
                <line x1="62" y1="257" x2="62" y2="245" stroke="#F5B942" strokeWidth="2.5" strokeLinecap="round" />
                <text x="90" y="252" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="800" fill={stripHeading}><tspan fill="#E0A020">20 min</tspan> &#8594; 30 sec</text>
                <text x="90" y="270" fontFamily="Inter, sans-serif" fontSize="9.5" fontWeight="500" fill={stripSub}>paperwork on every single sale</text>
            </g>

            {/* No SMS OTP strip */}
            <g className="gwx-float-alt">
                <rect x="246" y="228" width="204" height="58" rx="14" fill={stripBg} stroke={stripStroke} />
                <rect x="263" y="237" width="26" height="40" rx="5" fill="none" stroke="#ff8fa0" strokeWidth="2.5" />
                <text x="276" y="252" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="7.5" fontWeight="800" fill="#ff8fa0" letterSpacing="0.5">OTP</text>
                <text x="276" y="263" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="7" fontWeight="700" fill="rgba(255,143,160,0.8)" letterSpacing="1">4291</text>
                <line className="gwx-otp-slash" x1="258" y1="233" x2="294" y2="281" stroke="#ff5c72" strokeWidth="3.5" strokeLinecap="round" />
                <text x="302" y="252" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="800" fill={stripHeading}>No <tspan fill="#ff5c72">SMS OTP</tspan></text>
                <text x="302" y="270" fontFamily="Inter, sans-serif" fontSize="9.5" fontWeight="500" fill={stripSub}>no more calls to the owner</text>
            </g>

            {/* Road */}
            <line x1="20" y1="396" x2="460" y2="396" stroke={roadBase} strokeWidth="2" />
            <line className="gwx-road" x1="20" y1="396" x2="460" y2="396" stroke={roadDash} strokeWidth="2" strokeDasharray="14 18" />

            {/* Truck */}
            <g>
                <g className="gwx-truck-body">
                    <rect x="300" y="330" width="96" height="46" rx="5" fill="#635BFF" />
                    <path d="M306 330 L390 330 L382 318 L314 318 Z" fill="#7a73ff" />
                    <path d="M396 376 L396 340 C396 336 399 333 403 333 L424 333 C427 333 430 335 432 338 L442 356 C443 358 444 360 444 363 L444 376 Z" fill="#42A5F5" />
                    <path d="M404 340 L422 340 C424 340 426 341 427 343 L434 356 L404 356 Z" fill="#dbeafe" />
                    <rect x="310" y="339" width="80" height="26" rx="4" fill="#ffffff" opacity="0.95" />
                    <text x="350" y="350" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="800" fill="#0a2540">E-WAY READY</text>
                    <path d="M318 356.5 L321 359.5 L326 354" stroke="#00D4AA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <text x="330" y="360.5" fontFamily="Inter, sans-serif" fontSize="6.8" fontWeight="700" fill="#00695C">GSTIN VERIFIED</text>
                </g>
                <g className="gwx-wheel" style={{ transformOrigin: "322px 382px" }}>
                    <circle cx="322" cy="382" r="13" fill="#0a2540" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                    <line x1="322" y1="373" x2="322" y2="391" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                    <line x1="313" y1="382" x2="331" y2="382" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                </g>
                <g className="gwx-wheel" style={{ transformOrigin: "372px 382px" }}>
                    <circle cx="372" cy="382" r="13" fill="#0a2540" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                    <line x1="372" y1="373" x2="372" y2="391" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                    <line x1="363" y1="382" x2="381" y2="382" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                </g>
                <g className="gwx-wheel" style={{ transformOrigin: "424px 382px" }}>
                    <circle cx="424" cy="382" r="13" fill="#0a2540" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
                    <line x1="424" y1="373" x2="424" y2="391" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                    <line x1="415" y1="382" x2="433" y2="382" stroke="rgba(255,255,255,0.55)" strokeWidth="1.8" />
                </g>
            </g>
        </svg>
    );
}
